import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera } from 'iconsax-react';
import ProductImageUploader from '../../../components/Util/ProductImageUploader';
import chiediConferma from '../../../components/Util/chiediConferma';
import segnalaMancanti from '../../../components/Util/campiMancanti';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { aggiornaPostPerTutti, creaPostPerTutti, eliminaPostPerTutti, leggiPostPerTutti } from '../../../../api-client/appPlayer';
import { LIST_URL, messaggioRotta } from './index';

/*
 * Scheda di un post per tutti: titolo, testo, luogo (facoltativo) e immagine. «Pubblica» lo mette nel feed
 * di tutti gli utenti; sulla scheda di un post gia` pubblicato «Salva» ne cambia i testi e «Elimina» lo toglie.
 */

const MAX_TITOLO = 100;
const MAX_TESTO = 2000;
const MAX_LUOGO = 80;

export const SchedaPostPerTutti = () => {
    const { id } = useParams();
    const nuovo = id === 'nuovo';
    const navigate = useNavigate();
    const [post, setPost] = useState(nuovo ? {} : null);
    const [form, setForm] = useState({ title: '', body: '', location: '' });
    const [immagine, setImmagine] = useState(null); // base64 del nuovo ritaglio
    const [uploader, setUploader] = useState(false);
    const [errore, setErrore] = useState('');
    const [inCorso, setInCorso] = useState(false);

    useEffect(() => {
        if (nuovo) return;
        leggiPostPerTutti(id).then((r) => {
            if (!r || r.error) { setErrore(messaggioRotta(r)); setPost({}); return; }
            setPost(r);
            setForm({ title: r.title || '', body: r.body || '', location: r.location || '' });
        });
    }, [id, nuovo]);

    usePageTitle(nuovo ? 'Nuovo post' : post?.title);

    const anteprima = immagine ? `data:image/jpeg;base64,${immagine}` : post?.imageUrl;
    const cambiato = nuovo || (post && (form.title !== (post.title || '') || form.body !== (post.body || '') || form.location !== (post.location || '') || !!immagine));

    const salva = async () => {
        const mancanti = [];
        if (!form.title.trim()) mancanti.push({ campo: 'pt-titolo', messaggio: 'Manca il titolo del post' });
        if (!form.body.trim()) mancanti.push({ campo: 'pt-testo', messaggio: 'Manca il testo del post' });
        if (nuovo && !immagine) mancanti.push({ campo: 'pt-immagine', messaggio: 'Manca l\'immagine del post' });
        if (segnalaMancanti(mancanti) > 0) return;
        setInCorso(true);
        // Luogo vuoto: nel nuovo post non si manda, in modifica '' lo toglie (il backend scarta i null dal corpo)
        const luogo = form.location.trim();
        const dati = {
            title: form.title.trim(),
            body: form.body.trim(),
            ...(luogo || !nuovo ? { location: luogo } : {}),
            ...(immagine ? { image: immagine } : {}),
        };
        const r = nuovo ? await creaPostPerTutti(dati) : await aggiornaPostPerTutti(id, dati);
        setInCorso(false);
        if (r?.error) { setErrore(messaggioRotta(r)); return; }
        navigate(LIST_URL);
    };

    const elimina = async () => {
        if (!(await chiediConferma({ titolo: 'Eliminare il post?', testo: 'Il post sparisce dal feed di tutti gli utenti.', conferma: 'Elimina' }))) return;
        setInCorso(true);
        const r = await eliminaPostPerTutti(id);
        setInCorso(false);
        if (r?.error) { setErrore(messaggioRotta(r)); return; }
        navigate(LIST_URL);
    };

    if (!post) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>
            <ProductImageUploader
                show={uploader}
                onClose={() => setUploader(false)}
                onSave={(b64) => { setUploader(false); setImmagine(b64); }}
                titolo="Immagine del post"
                larghezza={1280}
                altezza={720}
            />
            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <Form.Label htmlFor="pt-titolo">Titolo *</Form.Label>
                    <Form.Control id="pt-titolo" value={form.title} maxLength={MAX_TITOLO} onChange={(e) => setForm({ ...form, title: e.target.value })} />

                    <Form.Label htmlFor="pt-testo" className="mt-4">Testo *</Form.Label>
                    <Form.Control as="textarea" id="pt-testo" rows={7} value={form.body} maxLength={MAX_TESTO} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                    <Form.Text className="text-dark">{form.body.length}/{MAX_TESTO}</Form.Text>

                    <Form.Label htmlFor="pt-luogo" className="mt-4">Luogo (facoltativo)</Form.Label>
                    <Form.Control id="pt-luogo" value={form.location} maxLength={MAX_LUOGO} onChange={(e) => setForm({ ...form, location: e.target.value })} />

                    <h5 className="sid-form-section">{nuovo ? 'Immagine *' : 'Immagine'}</h5>
                    <div className="sid-galleria__griglia">
                        {anteprima && (
                            <div className="sid-galleria__foto">
                                <div className="sid-galleria__immagine"><img src={anteprima} alt="" /></div>
                            </div>
                        )}
                        <button type="button" id="pt-immagine" className="sid-galleria__aggiungi" aria-label={anteprima ? 'Cambia immagine' : 'Aggiungi immagine'} onClick={() => setUploader(true)}>
                            <Camera size={28} />
                        </button>
                    </div>
                </Card.Body>
            </Card>
            <div className="d-flex justify-content-between mt-4">
                {!nuovo ? <Button className="btn btn-outline-danger" onClick={elimina} disabled={inCorso}>Elimina</Button> : <div />}
                <div className="d-flex gap-4">
                    <Button className="btn btn-light" onClick={() => navigate(LIST_URL)}>Annulla</Button>
                    <Button className="btn btn-primary" onClick={salva} disabled={inCorso || !cambiato}>{nuovo ? 'Pubblica' : 'Salva'}</Button>
                </div>
            </div>
        </>
    );
};

export default SchedaPostPerTutti;
